import { Box, Center, Flex, Grid, Stack } from "@chakra-ui/layout";
import { Spinner } from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import { uniqBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AppSelect, SelectItem } from "../../components/AppSelect";
import { NftItem } from "../../components/NftItem";
import { colors } from "../../core/constants/colors";
import { I18N_NAMESPACE } from "../../core/constants/i18n";
import { INft } from "../../core/models/nft";
import { nftService } from "../../services/nft.service";

function Assets() {
  const { account } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.PROFILE]);
  const params = useParams();

  const orderByOptions = useMemo(
    () => [
      { value: "none", label: t("None") },
      { value: "price-asc", label: t("Price: Low to High") },
      { value: "price-desc", label: t("Price: High to Low") },
      // { value: "date-desc", label: "Recently Listed" },
    ],
    [t]
  );

  const [selectedCollectionAddress, setSelectedCollectionAddress] = useState<SelectItem>();
  const [orderBy, setOrderBy] = useState<SelectItem | undefined>(orderByOptions.find((o) => o.value === "price-desc"));
  const [nfts, setNfts] = useState<INft[]>([]);
  const [isFetchingListedNfts, setIsFetchingListedNfts] = useState(false);

  const isOwned = useMemo(
    () => !params.id || params.id === "owned" || params.id?.toLowerCase() === account?.toLowerCase(),
    [params.id, account]
  );
  const id = useMemo(() => (isOwned ? account : params.id), [isOwned, account, params.id]);

  const filteredNfts = useMemo(() => {
    return nfts
      .filter(
        (i) =>
          !selectedCollectionAddress ||
          selectedCollectionAddress.value === "all" ||
          i.token_address === selectedCollectionAddress.value
      )
      .sort((a, b) => {
        if (!orderBy?.value) return 0;

        const aListedInfo = nfts.find((i) => i.token_address === a.token_address && i.token_id === a.token_id);
        const bListedInfo = nfts.find((i) => i.token_address === b.token_address && i.token_id === b.token_id);

        if (orderBy.value === "price-asc") {
          return (aListedInfo?.price || 0) - (bListedInfo?.price || 0);
        } else if (orderBy.value === "price-desc") {
          return (bListedInfo?.price || 0) - (aListedInfo?.price || 0);
        } else if (orderBy.value === "date-desc") {
          return 0;
        } else {
          return 0;
        }
      });
  }, [selectedCollectionAddress, nfts, orderBy]);

  const collections = useMemo(
    () =>
      uniqBy(nfts, (nft) => nft.token_address).map((i) => ({
        value: i.token_address,
        label: i.token_name || "",
      })),
    [nfts]
  );

  useEffect(() => {
    async function getListedNfts() {
      if (!id) return;
      setIsFetchingListedNfts(true);
      const response = await nftService.getNtfsByAccount(id);
      setNfts(response.data || []);
      setIsFetchingListedNfts(false);
    }

    if (id) {
      getListedNfts();
    }
  }, [id]);

  return (
    <div>
      <Stack 
        direction={{ base: "column", md: "row" }}
        justifyContent={{ base: "stretch", md: "flex-end" }}
        marginBottom={8} gap={{ base: 3, md: 6 }}>
        <Box>
          <AppSelect
            options={[{ value: "all", label: t("All") }, ...collections]}
            value={selectedCollectionAddress}
            placeholder={t("Collections")}
            onChange={setSelectedCollectionAddress}
          />
        </Box>
        <Box>
          <AppSelect options={orderByOptions} value={orderBy} placeholder={t('"Sort By"')} onChange={setOrderBy} />
        </Box>
      </Stack>
      {isFetchingListedNfts ? (
        <Flex alignItems="center" justifyContent="center" marginTop="40px">
          <Spinner thickness="5px" speed="0.65s" emptyColor="gray.200" color={colors.primary} size="xl" />
        </Flex>
      ) : filteredNfts.length === 0 ? (
        <Box>
          <Center fontSize="20px" marginTop={"30px"} color={"rgba(4, 4, 5, 0.6)"}>
            {t("No NFTs found")}
          </Center>
        </Box>
      ) : (
        <>
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xl: "repeat(4, 1fr)",
              "2xl": "repeat(5, 1fr)"
            }}
            gap={6}>
            {filteredNfts.map((nft) => {
              return (
                <NftItem
                  key={`${nft.token_address}-${nft.token_id}-${nft.owner_of}`}
                  bTokenAddress={nft?.b_token_address}
                  price={nft?.price}
                  isListed={nft?.status === "listed"}
                  tokenAddress={nft.token_address}
                  tokenId={nft.token_id}
                  tokenUri={nft.token_uri}
                  collectionName={nft.token_name}
                  image={nft?.image}
                  nftName={nft?.name}
                  ownerOf={nft.owner_of}
                  favorites={nft.favorites}
                  isFavorite={nft.is_favorite}
                />
              );
            })}
          </Grid>
        </>
      )}
    </div>
  );
}

export default React.memo(Assets);
